// Activo Fijo → Catálogos → Categorías / Clases (NIC 16 + LORTI).
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Categoria, METODOS, Modal, Switch, af, fieldCls, metodoLabel } from './shared';

const vacio = { codigo: '', nombre: '', depreciable: true, metodo: 'linea_recta', vida_util_meses: 120, valor_residual_pct: 0, tarifa_fiscal_pct: 10, es_activo: true };

export function CategoriasAF() {
  const { t } = useTranslation('activofijo');
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Categoria[]>({
    queryKey: ['af-categorias'],
    queryFn: () => af.list<Categoria>('categorias', '?select=*&deleted_at=is.null&order=codigo'),
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const [fNombre, setFNombre] = useState('');
  const set = (p: Partial<typeof vacio>) => setForm((f) => ({ ...f, ...p }));
  const filtrados = useMemo(() => rows.filter((c) => !fNombre.trim() || c.nombre.toLowerCase().includes(fNombre.trim().toLowerCase()) || (c.codigo ?? '').toLowerCase().includes(fNombre.trim().toLowerCase())), [rows, fNombre]);

  const abrir = (c?: Categoria) => {
    if (c) setForm({ codigo: c.codigo ?? '', nombre: c.nombre, depreciable: c.depreciable, metodo: c.metodo, vida_util_meses: c.vida_util_meses ?? 0, valor_residual_pct: Number(c.valor_residual_pct), tarifa_fiscal_pct: Number(c.tarifa_fiscal_pct ?? 0), es_activo: c.es_activo });
    else setForm(vacio);
    setEditId(c?.id ?? null);
    setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => {
      const body = {
        codigo: form.codigo || null, nombre: form.nombre.trim(), depreciable: form.depreciable,
        metodo: form.metodo, vida_util_meses: form.depreciable ? Number(form.vida_util_meses) || null : null,
        valor_residual_pct: Number(form.valor_residual_pct) || 0,
        tarifa_fiscal_pct: form.depreciable ? Number(form.tarifa_fiscal_pct) || null : null,
        es_activo: form.es_activo,
      };
      return editId ? af.update('categorias', editId, body) : af.create('categorias', body);
    },
    onSuccess: () => { toast.success(t('categorias.toast_guardada')); setAbierto(false); void qc.invalidateQueries({ queryKey: ['af-categorias'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => af.remove('categorias', id),
    onSuccess: () => { toast.success(t('categorias.toast_eliminada')); void qc.invalidateQueries({ queryKey: ['af-categorias'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Layers className="w-5 h-5" /> {t('categorias.titulo')}</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> {t('comun.nueva')}</Button>
      </div>
      <p className="text-sm text-muted-foreground">{t('categorias.intro')}</p>
      <input className={`${fieldCls} w-64`} placeholder={t('categorias.filtrar_ph')} value={fNombre} onChange={(e) => setFNombre(e.target.value)} />
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t('categorias.n_categorias', { n: filtrados.length })}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">{t('comun.codigo')}</th><th className="py-2 pr-3">{t('comun.nombre')}</th><th className="py-2 pr-3">{t('categorias.col_deprecia')}</th><th className="py-2 pr-3">{t('comun.metodo')}</th><th className="py-2 pr-3 text-right">{t('categorias.col_vida')}</th><th className="py-2 pr-3 text-right">{t('categorias.col_residual')}</th><th className="py-2 pr-3 text-right">{t('categorias.col_tarifa')}</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filtrados.length === 0 && !isFetching && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">{t('categorias.sin_categorias')}</td></tr>}
              {filtrados.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{c.codigo ?? '—'}</td>
                  <td className="py-2 pr-3 font-medium">{c.nombre}{!c.es_activo && <span className="ml-2 text-xs text-muted-foreground">{t('categorias.inactiva')}</span>}</td>
                  <td className="py-2 pr-3">{c.depreciable ? t('comun.si') : t('comun.no')}</td>
                  <td className="py-2 pr-3">{c.depreciable ? metodoLabel(c.metodo) : '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono">{c.depreciable ? (c.vida_util_meses ?? '—') : '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono">{Number(c.valor_residual_pct)}</td>
                  <td className="py-2 pr-3 text-right font-mono">{c.depreciable ? (c.tarifa_fiscal_pct ?? '—') : '—'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title={t('comun.editar')} onClick={() => abrir(c)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title={t('comun.eliminar')} onClick={async () => { if (await dlg.confirmar(t('categorias.confirmar_eliminar', { nombre: c.nombre }), { peligro: true })) eliminar.mutate(c.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? t('categorias.editar') : t('categorias.nueva')} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Campo label={t('comun.codigo')}><input className={fieldCls} value={form.codigo} onChange={(e) => set({ codigo: e.target.value.toUpperCase() })} /></Campo>
              <Campo label={t('comun.nombre_req')} className="col-span-2"><input className={fieldCls} value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} /></Campo>
            </div>
            <Switch checked={form.depreciable} onChange={(v) => set({ depreciable: v })} label={t('categorias.es_depreciable')} />
            {form.depreciable && (
              <div className="grid grid-cols-2 gap-3">
                <Campo label={t('comun.metodo')}><select className={fieldCls} value={form.metodo} onChange={(e) => set({ metodo: e.target.value })}>{METODOS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}</select></Campo>
                <Campo label={t('comun.vida_util_meses')}><input type="number" min="1" className={fieldCls} value={form.vida_util_meses} onChange={(e) => set({ vida_util_meses: Number(e.target.value) })} /></Campo>
                <Campo label={t('categorias.f_residual')}><input type="number" step="0.01" min="0" max="100" className={fieldCls} value={form.valor_residual_pct} onChange={(e) => set({ valor_residual_pct: Number(e.target.value) })} /></Campo>
                <Campo label={t('categorias.f_tarifa')}><input type="number" step="0.01" min="0" max="100" className={fieldCls} value={form.tarifa_fiscal_pct} onChange={(e) => set({ tarifa_fiscal_pct: Number(e.target.value) })} /></Campo>
              </div>
            )}
            <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label={t('comun.activa')} />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>{t('comun.cancelar')}</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>{t('comun.guardar')}</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
